// src/pages/NotificationsPage.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc, deleteDoc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { formatRelativeTime } from '../utils/styleUtils';
import styles from '../styles/NotificationsPage.module.css';
import { FiTrash2, FiCheck, FiBell, FiCheckCircle } from 'react-icons/fi';

const NotificationsPage = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [updatingId, setUpdatingId] = useState(null);
    const [isUpdatingAll, setIsUpdatingAll] = useState(false);

    const isMounted = useRef(true);
    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    // Fetch notifications in real-time
    useEffect(() => {
        if (!currentUser) {
            if (isMounted.current) {
                setLoading(false);
                setNotifications([]);
                setError("Please log in to view notifications.");
            }
            return;
        }

        if (isMounted.current) {
            setLoading(true);
            setError('');
        }

        const notificationsRef = collection(db, 'notifications');
        const q = query(
            notificationsRef,
            where('userId', '==', currentUser.uid),
            orderBy('timestamp', 'desc')
        );

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            if (!isMounted.current) return;
            const fetchedNotifications = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                timestamp: doc.data().timestamp?.toDate()
            }));
            setNotifications(fetchedNotifications);
            setLoading(false);
            setError('');
        }, (err) => {
            if (!isMounted.current) return;
            console.error("Error fetching notifications:", err);
            setError("Failed to load notifications. Please try refreshing.");
            setLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser]);

    // Mark a single notification as read
    const markAsRead = useCallback(async (notificationId) => {
        if (!isMounted.current || !notificationId || updatingId || isUpdatingAll || !currentUser) return;

        const originalNotifications = [...notifications];
        setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n));
        setUpdatingId(notificationId);
        setError('');

        try {
            const notificationRef = doc(db, 'notifications', notificationId);
            await updateDoc(notificationRef, { isRead: true });
        } catch (err) {
            console.error(`Error marking ${notificationId} as read:`, err);
            if (isMounted.current) {
                setError("Failed to update notification status.");
                setNotifications(originalNotifications);
            }
        } finally {
             if (isMounted.current) setUpdatingId(null);
        }
    }, [currentUser, notifications, updatingId, isUpdatingAll]);

    // Mark ALL unread notifications as read
    const markAllAsRead = useCallback(async () => {
        if (!isMounted.current || !currentUser || updatingId || isUpdatingAll) return;
        const unread = notifications.filter(n => !n.isRead);
        if (unread.length === 0) return;

        setIsUpdatingAll(true);
        setError('');
        const originalNotifications = [...notifications];
        setNotifications(prev => prev.map(n => n.isRead ? n : { ...n, isRead: true }));

        try {
            const batch = writeBatch(db);
            unread.forEach(notification => {
                if (notification.id) {
                    const notificationRef = doc(db, 'notifications', notification.id);
                    batch.update(notificationRef, { isRead: true });
                }
            });
            await batch.commit();
        } catch (err) {
            console.error("Error marking all notifications as read:", err);
            if (isMounted.current) {
                setError("Failed to mark all notifications as read.");
                setNotifications(originalNotifications);
            }
        } finally {
             if (isMounted.current) setIsUpdatingAll(false);
        }
    }, [currentUser, notifications, updatingId, isUpdatingAll]);

    // Delete a single notification
    const deleteNotification = useCallback(async (e, notificationId, notificationMessage) => {
        e.stopPropagation();
        if (!isMounted.current || !notificationId || updatingId || isUpdatingAll || !currentUser) return;

        const shortMessage = notificationMessage?.substring(0, 50) || 'this notification';
        if (!window.confirm(`Are you sure you want to delete the notification: "${shortMessage}..."?`)) {
            return;
        }

        const originalNotifications = [...notifications];
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        setUpdatingId(notificationId);
        setError('');

        try {
            const notificationRef = doc(db, 'notifications', notificationId);
            await deleteDoc(notificationRef);
        } catch (err) {
            console.error(`Error deleting notification ${notificationId}:`, err);
            if (isMounted.current) {
                setError("Failed to delete notification.");
                setNotifications(originalNotifications);
            }
        } finally {
            if (isMounted.current) setUpdatingId(null);
        }
    }, [currentUser, notifications, updatingId, isUpdatingAll]);
    
    // ** MODIFIED: Handle clicking on any type of notification **
    const handleNotificationClick = useCallback((notification) => {
        if (!notification.isRead) {
            markAsRead(notification.id);
        }
        
        // Check for a generic link first (like for daily logs)
        if (notification.link) {
            navigate(notification.link);
        } 
        // Else, check for a taskId to link to a task detail page
        else if (notification.taskId) {
            navigate(`/task/${notification.taskId}`);
        }
        // If neither exists, clicking does nothing
    }, [markAsRead, navigate]);


    const unreadCount = useMemo(() => notifications.filter(n => !n.isRead).length, [notifications]);
    
    const groupedNotifications = useMemo(() => {
        const groups = { today: [], yesterday: [], earlier: [] };
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        
        const isSameDay = (d1, d2) => 
            d1.getFullYear() === d2.getFullYear() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getDate() === d2.getDate();

        notifications.forEach(n => {
            if (!n.timestamp) {
                groups.earlier.push(n);
                return;
            }
            if (isSameDay(n.timestamp, today)) {
                groups.today.push(n);
            } else if (isSameDay(n.timestamp, yesterday)) {
                groups.yesterday.push(n);
            } else {
                groups.earlier.push(n);
            }
        });
        return groups;
    }, [notifications]);

    const NotificationItem = ({ notification }) => {
        // A notification is considered "clickable" if it has either a link or a taskId
        const isClickable = !!notification.link || !!notification.taskId;
        
        return (
            <li
                className={`${styles.notificationItem} ${notification.isRead ? styles.read : styles.unread} ${updatingId === notification.id ? styles.itemUpdating : ''} ${isClickable ? styles.clickable : ''}`}
                onClick={isClickable ? () => handleNotificationClick(notification) : undefined}
                tabIndex={isClickable ? 0 : -1}
                onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') handleNotificationClick(notification); } : undefined}
            >
                 <div className={styles.notificationContent}>
                    {!notification.isRead && <div className={styles.unreadDot} title="Unread"></div>}
                    <div className={styles.messageDetails}>
                        <span className={styles.message}>{notification.message || 'Notification received.'}</span>
                        <span className={styles.timestamp} title={notification.timestamp?.toLocaleString()}>
                            {notification.timestamp ? formatRelativeTime(notification.timestamp) : 'Receiving...'}
                        </span>
                    </div>
                </div>
                <div className={styles.notificationActions}>
                    {!notification.isRead && (
                        <button
                            onClick={(e) => { e.stopPropagation(); markAsRead(notification.id); }}
                            className={`${styles.actionButton} ${styles.readButton}`}
                            disabled={updatingId === notification.id || isUpdatingAll}
                            title="Mark as Read"
                        >
                            {updatingId === notification.id && !isUpdatingAll ? <span className={styles.loadingSpinnerSmallInline}></span> : <FiCheck />}
                        </button>
                    )}
                    <button
                        onClick={(e) => deleteNotification(e, notification.id, notification.message)}
                        className={`${styles.actionButton} ${styles.deleteButton}`}
                        disabled={updatingId === notification.id || isUpdatingAll}
                        title="Delete Notification"
                    >
                        {updatingId === notification.id && !isUpdatingAll ? <span className={styles.loadingSpinnerSmallInline}></span> : <FiTrash2 />}
                    </button>
                </div>
            </li>
        );
    };

    return (
        <div className={styles.pageContainer}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h2 className={styles.title}>
                        Notifications
                        {unreadCount > 0 && <span className={styles.countPill}>{unreadCount}</span>}
                    </h2>
                    <p className={styles.subtitle}>Stay up to date with tasks, approvals and requests.</p>
                </div>
                <button
                    onClick={markAllAsRead}
                    className={styles.markAllButton}
                    disabled={unreadCount === 0 || loading || isUpdatingAll || !!updatingId}
                    title={unreadCount === 0 ? "No unread notifications" : "Mark all unread notifications as read"}
                >
                    {isUpdatingAll ? (
                        <span className={styles.loadingSpinnerSmall} aria-label="Marking all as read"></span>
                    ) : (
                        <><FiCheckCircle /> Mark all read</>
                    )}
                </button>
            </div>

            {error && <div className={styles.errorMessage}>{error}</div>}

            {loading ? (
                <div className={styles.loadingIndicator}>Loading notifications...</div>
            ) : (
                <div className={styles.notificationListContainer}>
                    {notifications.length === 0 && !error && (
                        <div className={styles.noNotifications}>
                            <FiBell className={styles.emptyIcon} />
                            <p>You have no notifications.</p>
                            <span>New updates will appear here.</span>
                        </div>
                    )}
                    {groupedNotifications.today.length > 0 && (
                        <div className={styles.notificationGroup}>
                            <h3 className={styles.groupTitle}>Today</h3>
                            <ul className={styles.notificationList}>
                                {groupedNotifications.today.map(n => <NotificationItem key={n.id} notification={n} />)}
                            </ul>
                        </div>
                    )}
                     {groupedNotifications.yesterday.length > 0 && (
                        <div className={styles.notificationGroup}>
                            <h3 className={styles.groupTitle}>Yesterday</h3>
                            <ul className={styles.notificationList}>
                                {groupedNotifications.yesterday.map(n => <NotificationItem key={n.id} notification={n} />)}
                            </ul>
                        </div>
                    )}
                     {groupedNotifications.earlier.length > 0 && (
                        <div className={styles.notificationGroup}>
                            <h3 className={styles.groupTitle}>Earlier</h3>
                            <ul className={styles.notificationList}>
                                {groupedNotifications.earlier.map(n => <NotificationItem key={n.id} notification={n} />)}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationsPage;